// Résolution du lieu opérationnel par défaut (stock / vente / réception)
import type { Business } from '../types/business';
import { canSellFromAnySite, getSalesSiteUiLabels } from '../types/business';
import type { Site } from '../types/site';

const activeSites = (sites: Site[]): Site[] => sites.filter((s) => s.isActive);

/** Lieu favori employé s’il est actif dans la liste. */
export const resolveFavoriteSite = (
  sites: Site[],
  favoriteSiteId?: string | null
): Site | null => {
  if (!favoriteSiteId) return null;
  return activeSites(sites).find((s) => s.id === favoriteSiteId) ?? null;
};

/**
 * Lieu par défaut pour initialiser le stock, mouvements, ventes, OF, etc.
 * - `pos_only` : premier point de vente actif (comportement historique)
 * - `any_site` : POS s’il existe, sinon premier lieu actif (entrepôt OK)
 */
export const resolveDefaultSalesOrStockSite = (
  sites: Site[],
  business: Business | null
): Site | null => {
  const active = activeSites(sites);
  if (active.length === 0) return null;

  const pos = active.find((s) => s.type === 'point_of_sale');
  if (canSellFromAnySite(business)) {
    return pos ?? active[0] ?? null;
  }
  return pos ?? null;
};

/**
 * Lieu de réception par défaut (approvisionnements).
 * - `any_site` : entrepôt d’abord, puis POS, puis tout lieu actif
 * - `pos_only` : entrepôt s’il existe, sinon POS (historique)
 * Préférer `resolveFavoriteSite` en amont si un favori employé existe.
 */
export const resolveDefaultReceivingSite = (
  sites: Site[],
  business: Business | null
): Site | null => {
  const active = activeSites(sites);
  if (active.length === 0) return null;

  const warehouse = active.find((s) => s.type === 'warehouse');
  const pos = active.find((s) => s.type === 'point_of_sale');

  if (canSellFromAnySite(business)) {
    return warehouse ?? pos ?? active[0] ?? null;
  }
  return warehouse ?? pos ?? null;
};

export const getMissingDefaultSiteError = (business: Business | null): string =>
  getSalesSiteUiLabels(business).missingSalesSite;
